<!-- PaginationButtons.vue -->

<script setup>
import { computed, nextTick, ref, watch } from "vue";

const props = defineProps({
  currentPage: { type: Number, required: true },
  pageSize: { type: Number, required: true },
  totalCount: { type: Number, required: true },
  totalPages: { type: Number, required: true },
});

const emit = defineEmits(["change-page"]);

const ELLIPSIS = "...";

// Página atual saneada: durante um refetch o pai pode passar, por um tick, uma
// página fora do intervalo (ex.: excluir os últimos itens reduz totalPages antes
// de currentPage ser corrigido). Sem isto a janela abaixo geraria números inválidos.
const safeCurrentPage = computed(() => {
  const total = props.totalPages;
  if (total < 1) return 1;
  return Math.min(Math.max(props.currentPage, 1), total);
});

function goToPage(page) {
  if (typeof page !== "number" || page < 1 || page > props.totalPages) return;
  emit("change-page", page);
}

function previousPage() {
  goToPage(safeCurrentPage.value - 1);
}

function nextPage() {
  goToPage(safeCurrentPage.value + 1);
}

/*
  REGRA:
  - Primeira e última página sempre visíveis.
  - Janela de 3 números ao redor da página atual, deslocada quando encosta nas
    bordas para manter a quantidade de slots estável.
  - "..." é inserido SOMENTE onde existe descontinuidade real entre dois números
    consecutivos da lista. Nunca por posição fixa — era isso que fazia o "..."
    aparecer entre páginas vizinhas (ex.: "1 5 6 ... 7 8") e, ao mesmo tempo,
    deixar a lacuna verdadeira sem marcação.
*/
const pages = computed(() => {
  const total = props.totalPages;
  const current = safeCurrentPage.value;

  if (total <= 6) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  let start = current - 1;
  let end = current + 1;

  // Desloca a janela para dentro sem encolher (1 e total já entram fixos).
  if (start < 2) {
    start = 2;
    end = 4;
  }
  if (end > total - 1) {
    end = total - 1;
    start = total - 3;
  }

  const numbers = [1];
  for (let page = start; page <= end; page++) numbers.push(page);
  numbers.push(total);

  const result = [];
  numbers.forEach((page, index) => {
    if (index > 0 && page - numbers[index - 1] > 1) result.push(ELLIPSIS);
    result.push(page);
  });

  return result;
});

/* =========================================================
   Salto direto de página: o "..." é clicável e vira um input.
   O affordance fica exatamente onde estão as páginas ocultas,
   sem consumir largura extra na linha do paginador.
  ========================================================= */
const jumpIndex = ref(null);
const jumpValue = ref("");
const jumpInputEl = ref(null);

function setJumpInput(el) {
  jumpInputEl.value = el;
}

async function openJump(index) {
  jumpIndex.value = index;
  jumpValue.value = "";

  await nextTick();
  jumpInputEl.value?.focus();
}

function closeJump() {
  jumpIndex.value = null;
  jumpValue.value = "";
}

function onJumpInput(event) {
  // Só dígitos, e um teto de caracteres para o campo não crescer sem limite.
  jumpValue.value = event.target.value.replace(/\D/g, "").slice(0, 7);
}

function confirmJump() {
  const raw = jumpValue.value;
  closeJump();

  if (!raw) return;

  // Clamp em vez de recusar: digitar "999" em 80 páginas leva à 80, que é a
  // intenção evidente do usuário.
  const target = Math.min(Math.max(parseInt(raw, 10), 1), props.totalPages);
  if (target !== props.currentPage) goToPage(target);
}

// Se a paginação mudar por qualquer outro caminho (setas, filtro, refetch),
// o input pendente perde o contexto — os índices de `pages` mudaram.
watch([() => props.currentPage, () => props.totalPages], closeJump);
</script>

<template>
  <div class="paginator-wrapper" v-if="totalPages > 0">
    <div class="paginator">
      <button
        type="button"
        @click="previousPage"
        :disabled="safeCurrentPage === 1"
        class="page-btn"
      >
        <i class="fas fa-chevron-left"></i>
      </button>

      <template v-for="(page, index) in pages" :key="index">
        <template v-if="page === '...'">
          <input
            v-if="jumpIndex === index"
            :ref="setJumpInput"
            type="text"
            inputmode="numeric"
            :value="jumpValue"
            :placeholder="String(totalPages)"
            :aria-label="`Ir para página (1 a ${totalPages})`"
            class="page-jump-input"
            @input="onJumpInput"
            @keydown.enter.prevent="confirmJump"
            @keydown.esc.prevent="closeJump"
            @blur="closeJump"
          />

          <button
            v-else
            type="button"
            class="page-btn-ellipsis"
            :title="`Ir para página (1 a ${totalPages})`"
            aria-label="Ir para página"
            @click="openJump(index)"
          >
            ...
          </button>
        </template>

        <button
          v-else
          type="button"
          @click="goToPage(page)"
          :class="{ 'page-btn': true, active: page === safeCurrentPage }"
          :aria-current="page === safeCurrentPage ? 'page' : undefined"
        >
          {{ page }}
        </button>
      </template>

      <button
        type="button"
        @click="nextPage"
        :disabled="safeCurrentPage === totalPages"
        class="page-btn"
      >
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>
  </div>
</template>

<style scoped>
.paginator-wrapper {
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  line-height: 1.15;
  color: var(--text-light);
  margin-left: 8px;
  margin-top: 6px;
  margin-bottom: 8px;
}

.paginator {
  display: flex;
  align-items: center;
  gap: 3px;
}

.page-btn-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  /* mesma largura do input de salto: ao alternar entre os dois nada se desloca
     na linha, então um clique iniciado sobre um número não "escorrega" para
     outro quando o input fecha no blur. */
  min-width: 34px;
  padding: 0 6px;
  color: var(--text-light);
  font-size: 0.82rem;
  line-height: 1;

  /* agora é <button>: neutraliza o estilo nativo, mantendo a aparência antiga */
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.page-btn-ellipsis:hover {
  background-color: var(--neutral-100);
  color: var(--text-dark);
}

.page-btn-ellipsis:focus-visible {
  outline: 2px solid var(--bg-active-nav);
  outline-offset: 1px;
}

.page-jump-input {
  width: 34px;
  height: 26px;
  padding: 0 4px;
  border: 1px solid var(--bg-active-nav);
  border-radius: 10px;
  background: var(--surface);
  color: var(--text-dark);
  font-weight: 600;
  font-size: 0.65rem;
  line-height: 1;
  text-align: center;
  outline: none;
}

.page-jump-input::placeholder {
  color: var(--text-light);
  font-weight: 500;
  opacity: 0.7;
}
</style>
